// useComps — the hand-written per-domain hooks over the generated client for the Antenna
// comps manager: granting free access to a paid tier and revoking it. Lives under app/hooks/
// (the lint-exempt wrapper dir), built on useBandQuery/useBandMutation so the key is
// ['band', bandId, 'antenna', 'comps'].
//
// Mutation posture: grant/revoke are confirm-then-repaint — NEVER optimistic. Each invalidates the
// list on server success so the manager repaints via a GET refetch.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

type CompView = components["schemas"]["CompView"];
type GrantCompRequest = components["schemas"]["GrantCompRequest"];

/** The comps key. */
export const compsKey = (bandId: string) => ["band", bandId, "antenna", "comps"] as const;

/**
 * GET the comps list. `antenna:manage`; a seat without it 403s → the query's error state. Born-blank
 * returns an empty list → the designed empty state.
 */
export function useComps() {
  const { api } = useApp();
  return useBandQuery<CompView[]>(
    ["antenna", "comps"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/antenna/comps",
        { params: { path: { bandId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data.data ?? [];
    },
    { staleTime: 15_000 },
  );
}

/** Grant a comp (POST → 201 | 400). Not optimistic; invalidates the list on success.*/
export function useGrantComp() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<CompView, GrantCompRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/antenna/comps",
        { params: { path: { bandId: band } }, body },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: compsKey(bandId) }) },
  );
}

/** Revoke a comp by email (DELETE /comps?email= → 204 | 404). Not optimistic; invalidates.*/
export function useRevokeComp() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<void, string>(
    async (band, email) => {
      const { error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/antenna/comps",
        { params: { path: { bandId: band }, query: { email } } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: compsKey(bandId) }) },
  );
}
